import Box from "@mui/material/Box";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import RoomIcon from "@mui/icons-material/Room";
import InfoIcon from "@mui/icons-material/Info";
import { actionInfoQueryAndSetData } from "./InfoRedux";
import { actionDataSearchRooms } from "../Data/dataRedux";
import { actionMapUpdateZoomFeatures } from "../Map/MapRedux";
import RoomActions from "./RoomActions";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";

import { CopyToClipboard } from "react-copy-to-clipboard";
import { actionAppBarFloorUpdated } from "../AppBar";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

const root_ = (theme) => ({
  "& .MuiTypography-overline": {
    lineHeight: 1.25,
    letterSpacing: "0.03em",
  },
});

const id_ = (theme) => ({
  color: "grey",
});

const stack_ = (theme) => ({
  display: "inline-block",
  "& .MuiButton-root": {
    lineHeight: 1,
  },
  "& .MuiTypography-overline": {
    lineHeight: 1,
  },
});

const dep_ = (theme) => ({
  fontSize: "0.65rem",
  letterSpacing: "0.01em !important",
  fontWeight: "bold",
  color: "rgb(0, 100, 177)",
});

const circleIcon_ = (theme) => ({
  color: "white !important",
  backgroundColor: "rgb(0, 100, 177)",
  borderRadius: "50%",
  letterSpacing: "0",
  display: "inline-block",
  width: 18,
  height: 18,
  fontSize: "0.55rem",
  lineHeight: "18px",
  textAlign: "center",
  marginLeft: "6px",
});

const title_ = (theme) => ({
  fontSize: "1.5rem",
  fontWeight: "bold",
  display: "inline-block",
  letterSpacing: "0 !important",
});

const subTitle_ = (theme) => ({
  fontSize: "1.0rem",
  color: "grey",
  display: "inline-block",
  letterSpacing: "0 !important",
});

const buildingBtn_ = (theme) => ({
  padding: "0px",
  minWidth: "auto",
  borderRadius: "0px",
  fontWeight: "400",
  color: "#4455b5",
  display: "block",
  fontSize: "0.6rem",
  marginBottom: "2px",
});

const primary_ = (theme) => ({
  fontWeight: "bold",
});

const secondary_ = (theme) => ({
  color: "grey",
  fontSize: "0.6rem",
  letterSpacing: "0 !important",
});

const actionIcon_ = (theme) => ({
  padding: "2px",
  marginRight: "2px",
  marginTop: "-3px",
  fontSize: "1rem",
  "& .MuiSvgIcon-root": {
    fontSize: "1rem",
  },
});

const clickable_ = (theme) => ({
  color: "rgb(0, 100, 177)",
  "&:hover": {
    textDecoration: "underline",
  },
});

export default function BasicInfo({ room }) {
  const dispatch = useDispatch();
  const { roomtypes } = useSelector((state) => state.searchReducer.defaultData);

  const {
    rmnbr,
    rmrecnbr,
    clinrmnbr,
    bld_descrshort,
    bldrecnbr,
    deptid,
    dept_descr,
    floor,
    rmsqrft,
    rmstationcnt,
    rmtyp,
    rmtyp_descrshort,
    rmsubtyp,
    rmsubtyp_descrshort,
  } = room;

  // compare descrshort strings for rmtyp and rmsubtyp to redux search data descr50 strings
  const abbrevData = {};
  const roomData = roomtypes && roomtypes.find((x) => x.rmtyp === rmtyp);
  if (
    roomData &&
    roomData.rmtyp_descr50 &&
    rmtyp_descrshort !== roomData.rmtyp_descr50
  )
    abbrevData.rmtyp = roomData.rmtyp_descr50;
  if (rmsubtyp_descrshort.trim()) {
    const rmsubtypData =
      roomData &&
      roomData.subtypes &&
      roomData.subtypes.find((x) => x.rmsubtyp === rmsubtyp);
    if (
      rmsubtypData &&
      rmsubtypData.rmsubtyp_descr50 &&
      rmsubtyp_descrshort !== rmsubtypData.rmsubtyp_descr50
    )
      abbrevData.rmsubtyp = rmsubtypData.rmsubtyp_descr50;
  }

  const defaultRRNTooltip = "Room Record Number";
  const [rmrecnbrLabel, setRmrecnmbrLabel] = useState(defaultRRNTooltip);
  const onCopyHandler = () => {
    setRmrecnmbrLabel(`${defaultRRNTooltip} Copied to Clipboard`);
    setTimeout(() => {
      setRmrecnmbrLabel(defaultRRNTooltip);
    }, 1500);
  };

  const defaultRNTooltip = "Room Number";
  const [rmnbrLabel, setRmnbrLabel] = useState(defaultRNTooltip);
  const onRmnbrCopyHandler = () => {
    setRmnbrLabel(`${defaultRNTooltip} Copied to Clipboard`);
    setTimeout(() => {
      setRmnbrLabel(defaultRNTooltip);
    }, 1500);
  };

  const defaultCRNTooltip = "Clinical Room Number";
  const [clinrmnbrLabel, setClinrmnbrLabel] = useState(defaultCRNTooltip);
  const onClinrmnbrCopyHandler = () => {
    setClinrmnbrLabel(`${defaultCRNTooltip} Copied to Clipboard`);
    setTimeout(() => {
      setClinrmnbrLabel(defaultCRNTooltip);
    }, 1500);
  };

  const zoomTo = () => {
    dispatch(actionMapUpdateZoomFeatures({ type: "rrn", data: rmrecnbr }));
    dispatch(actionAppBarFloorUpdated(floor));
  };
  const toBuildingInfo = () => {
    dispatch(actionInfoQueryAndSetData(bldrecnbr, "building"));
  };

  const searchDept = () => {
    const filters = [
      { type: "building", bldrecnbr, label: bld_descrshort },
      { type: "department", deptid, label: dept_descr },
    ];
    dispatch(actionDataSearchRooms(filters));
  };

  const searchRoomtype = () => {
    const filters = [
      { type: "building", bldrecnbr, label: bld_descrshort },
      { type: "roomtype", rmtyp, label: rmtyp_descrshort },
    ];
    dispatch(actionDataSearchRooms(filters));
  };

  const searchRoomsubtype = () => {
    const filters = [
      { type: "building", bldrecnbr, label: bld_descrshort },
      {
        type: "roomsubtype",
        rmtyp,
        rmsubtyp,
        label: `${rmtyp_descrshort} - ${rmsubtyp_descrshort}`,
      },
    ];
    dispatch(actionDataSearchRooms(filters));
  };

  return (
    <Box sx={root_}>
      <Box>
        <Tooltip describeChild title={rmrecnbrLabel}>
          <span>
            <CopyToClipboard text={rmrecnbr} onCopy={onCopyHandler}>
              <Typography
                component="span"
                variant="overline"
                sx={id_}
                data-testid="info-room-rmrecnbr"
              >
                {rmrecnbr}
              </Typography>
            </CopyToClipboard>
          </span>
        </Tooltip>
        <span> </span>{" "}
        {/* fix issue when double clicking bldrecnbr grabs DEP too */}
        <Box component="span" sx={circleIcon_}>
          DEP
        </Box>
        <Tooltip describeChild title="Survey Dept">
          <Button
            size="small"
            style={{
              padding: "0px",
              minWidth: "auto",
              marginLeft: "4px",
              fontSize: "inherit",
            }}
            onClick={searchDept}
          >
            <Typography
              component="span"
              variant="overline"
              sx={[dep_, clickable_]}
              data-testid="info-room-dept-name"
            >
              {dept_descr}
            </Typography>
          </Button>
        </Tooltip>
      </Box>
      <Box style={{ margin: "8px 0" }}>
        <Box sx={stack_}>
          <Box>
            <Tooltip describeChild title={rmnbrLabel}>
              <span>
                <CopyToClipboard text={rmnbr} onCopy={onRmnbrCopyHandler}>
                  <Typography
                    component="div"
                    variant="overline"
                    sx={title_}
                    data-testid="info-room-rmnbr"
                  >
                    {`RM ${rmnbr}`}
                  </Typography>
                </CopyToClipboard>
              </span>
            </Tooltip>
            <Tooltip title="Zoom In">
              <IconButton
                color="primary"
                onClick={zoomTo}
                sx={actionIcon_}
                size="large"
              >
                <RoomIcon />
              </IconButton>
            </Tooltip>
            <Box sx={stack_}>
              <Tooltip describeChild title="Building">
                <Button
                  size="small"
                  onClick={toBuildingInfo}
                  sx={[buildingBtn_, clickable_]}
                  data-testid="info-room-bld"
                >
                  {bld_descrshort}
                </Button>
              </Tooltip>
              <Typography
                component="div"
                variant="overline"
                sx={secondary_}
                data-testid="info-room-floor"
              >
                FLOOR {floor}
              </Typography>
            </Box>
          </Box>
          {clinrmnbr && (
            <Box>
              <Tooltip describeChild title={clinrmnbrLabel}>
                <span>
                  <CopyToClipboard
                    text={clinrmnbr}
                    onCopy={onClinrmnbrCopyHandler}
                  >
                    <Typography
                      component="div"
                      variant="overline"
                      sx={subTitle_}
                      data-testid="info-clin-room-rmnbr"
                    >
                      <LocalHospitalIcon
                        fontSize="small"
                        sx={{ verticalAlign: "text-bottom" }}
                      />
                      {`${clinrmnbr}`}
                    </Typography>
                  </CopyToClipboard>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
      <Box>
        <Box sx={stack_}>
          <Typography component="div" variant="overline">
            <Box component="span" sx={primary_} data-testid="info-room-sqft">
              {rmsqrft}
            </Box>
            <Box component="span" sx={secondary_} style={{ marginLeft: "2px" }}>
              sqft
            </Box>
          </Typography>
          <Typography component="div" variant="overline">
            <Box
              component="span"
              sx={primary_}
              data-testid="info-room-rmstationcnt"
            >
              {rmstationcnt}
            </Box>
            <Box component="span" sx={secondary_} style={{ marginLeft: "2px" }}>
              workstation{rmstationcnt !== 1 && "s"}
            </Box>
          </Typography>
        </Box>
        <Box sx={stack_} style={{ marginLeft: "16px" }}>
          <Typography component="div" variant="overline">
            <Box component="span" sx={secondary_}>
              ROOM TYPE{" "}
            </Box>
          </Typography>
          <Typography component="div" variant="overline">
            <Tooltip
              title={`Search ${rmtyp_descrshort.toUpperCase()} in ${bld_descrshort}`}
            >
              <Button
                size="small"
                style={{
                  padding: "0px",
                  minWidth: "auto",
                  fontSize: "inherit",
                }}
                onClick={searchRoomtype}
                sx={clickable_}
              >
                <Box
                  component="span"
                  sx={primary_}
                  data-testid="info-room-rmtyp-name"
                >
                  {rmtyp_descrshort}
                </Box>
              </Button>
            </Tooltip>
            <Box
              component="span"
              sx={secondary_}
              style={{ marginLeft: "2px" }}
              data-testid="info-room-rmtyp"
            >
              ({rmtyp})
            </Box>
          </Typography>
        </Box>
        {abbrevData && abbrevData.rmtyp && (
          <Box sx={stack_}>
            <Box style={{ display: "flex", justifyContent: "center" }}>
              <Tooltip title={abbrevData.rmtyp}>
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        {!!rmsubtyp.trim() && (
          <Box sx={stack_} style={{ marginLeft: "16px" }}>
            <Typography component="div" variant="overline">
              <Box component="span" sx={secondary_}>
                ROOM SUBTYPE{" "}
              </Box>
            </Typography>
            <Typography component="div" variant="overline">
              <Tooltip
                title={`Search ${rmsubtyp_descrshort.toUpperCase()} in ${bld_descrshort}`}
              >
                <Button
                  size="small"
                  style={{
                    padding: "0px",
                    minWidth: "auto",
                    fontSize: "inherit",
                  }}
                  onClick={searchRoomsubtype}
                  sx={clickable_}
                >
                  <Box
                    component="span"
                    sx={primary_}
                    data-testid="info-room-rmsubtyp-name"
                  >
                    {rmsubtyp_descrshort}
                  </Box>
                </Button>
              </Tooltip>
              <Box
                component="span"
                sx={secondary_}
                style={{ marginLeft: "2px" }}
                data-testid="info-room-rmsubtyp"
              >
                ({rmsubtyp})
              </Box>
            </Typography>
          </Box>
        )}
        {abbrevData && abbrevData.rmsubtyp && (
          <Box sx={stack_}>
            <Box style={{ display: "flex", justifyContent: "center" }}>
              <Tooltip title={abbrevData.rmsubtyp}>
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
      <RoomActions room={room} />
    </Box>
  );
}
